/**
 * 블록 직렬화 (P0-② / P1-①) — 모든 콘텐츠형 블록을 균일 처리.
 * BlockNote 블록 `{id,type,props,content,children}` ↔ DOM `.block` 요소.
 *
 * 설계: 타입별 props 스키마를 추측하지 않고 **props/children을 verbatim 보존**
 *   (요소 객체에 `__props`/`__children`로 보관) → paragraph/heading/리스트/quote/codeBlock
 *   등 모든 타입이 라운드트립 충실(EC1). content(주 편집 데이터)만 DOM(.content)에 둔다.
 *
 * 표(table)는 document.js가 별도 처리(라이브 <table> 렌더).
 */

import { inlineContentToHtml, htmlToInlineContent } from "./inline.js";

export const LIST_TYPES = new Set(["bulletListItem", "numberedListItem", "checkListItem"]);

/** 블록의 직속 자식 `.content` 요소 */
export function directContent(el) {
  return Array.from(el.children).find((c) => c.classList && c.classList.contains("content")) || null;
}

/**
 * 편집/렌더용 .block 요소 빌더. opts: { id?, props?, children?, innerHTML? }
 * (편집 동작에서 HTML 문자열로 새 블록을 만들 때도 사용)
 */
export function makeBlockElement(type, opts) {
  if (typeof document === "undefined") throw new Error("makeBlockElement: DOM(document) 필요");
  opts = opts || {};
  type = type || "paragraph";
  const props = opts.props || {};

  const el = document.createElement("div");
  el.className = "block";
  el.dataset.type = type;
  el.dataset.contentType = type; // BlockNote data-content-type 별칭 — 소비처 CSS([data-content-type=…]) 호환
  if (opts.id != null) el.dataset.id = String(opts.id);
  el.__props = opts.props ? { ...opts.props } : undefined; // verbatim
  el.__children = Array.isArray(opts.children) ? opts.children : [];

  // 렌더 힌트(직렬화엔 영향 없음 — 읽기는 항상 __props에서)
  if (props.level != null) el.dataset.level = String(props.level);
  // 번호목록 시작값(A7): counter-reset 으로 첫 항목 카운터를 start-1 로 맞춤(이후 increment 로 start 가 됨)
  if (type === "numberedListItem" && props.start != null && Number(props.start) > 1)
    el.style.counterReset = "ln " + (Number(props.start) - 1);
  if (props.textAlignment && props.textAlignment !== "left") el.dataset.textAlignment = props.textAlignment;
  if (props.textColor && props.textColor !== "default") el.dataset.textColor = props.textColor;
  if (props.backgroundColor && props.backgroundColor !== "default") el.dataset.backgroundColor = props.backgroundColor;

  // 토글(접을 수 있는) 블록 — heading(isToggleable, blocknote 호환) / toggleListItem: 좌측 캐럿으로 children 접기·펼치기
  const isToggle = props.isToggleable === true || type === "toggleListItem";
  if (isToggle) {
    el.classList.add("lumir-toggle-block");
    if (props.isToggleable) el.dataset.toggle = "true";
    const caret = document.createElement("button");
    caret.type = "button";
    caret.className = "lumir-toggle";
    caret.setAttribute("contenteditable", "false");
    caret.setAttribute("aria-label", "접기/펼치기");
    el.appendChild(caret);
  }

  if (type === "checkListItem") {
    const marker = document.createElement("div");
    marker.className = "marker";
    marker.setAttribute("contenteditable", "false");
    marker.innerHTML = '<span class="check" role="checkbox"></span>';
    el.appendChild(marker);
    if (props.checked) el.classList.add("checked");
  } else if (type === "toggleListItem" || LIST_TYPES.has(type)) {
    el.classList.add("list");
  }

  const content = document.createElement("div");
  content.className = "content";
  content.setAttribute("contenteditable", "true");
  content.innerHTML = opts.innerHTML != null ? opts.innerHTML : "";
  el.appendChild(content);
  return el;
}

/** BlockNote 블록 → DOM `.block` 요소 */
export function blockToElement(block) {
  return makeBlockElement((block && block.type) || "paragraph", {
    id: block && block.id,
    props: block && block.props,
    children: block && block.children,
    innerHTML: inlineContentToHtml(Array.isArray(block && block.content) ? block.content : []),
  });
}

/** DOM `.block` 요소 → BlockNote 블록 */
export function elementToBlock(el) {
  const block = {};
  if (el.dataset.id != null) block.id = el.dataset.id;
  block.type = el.dataset.type || "paragraph";
  if (el.__props !== undefined) block.props = el.__props;
  const contentEl = directContent(el);
  block.content = contentEl ? htmlToInlineContent(contentEl) : [];
  block.children = el.__children || [];
  return block;
}
